import React, { useMemo } from 'react';

const P = {
  navy:   '#1A2B6B',
  blue:   '#4BBDE0',
  pink:   '#E0498A',
  bg:     '#EBF6FB',
  bgCard: '#FFFFFF',
  text:   '#16305C',
  muted:  '#6B829E',
  border: '#CFE5F1',
};

const EMPTY_DATA = {
  client: '',
  campaign: '',
  period: '',
  overview: {},
  platformSplit: { facebook: {}, instagram: {} },
  devices: { android: {}, ios: {} },
  regionsAndroid: [],
  demographics: [],
};

/* ── formatters ── */
const fmt  = v => Number.isFinite(Number(v)) ? new Intl.NumberFormat('it-IT').format(Number(v)) : '—';
const fmtE = v => Number.isFinite(Number(v)) ? new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(Number(v)) : '—';
const fmtP = v => Number.isFinite(Number(v)) ? `${Number(v).toFixed(2)}%` : '—';

/* ── shared styles ── */
const card = {
  background: P.bgCard,
  border: `1px solid ${P.border}`,
  borderRadius: 12,
  padding: '18px 20px',
};

const sectionLabel = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  color: P.muted,
  marginBottom: 12,
  marginTop: 0,
};

/* ── sub-components ── */

function KpiCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#F0F6FA', borderRadius: 8, padding: '12px 14px', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: P.muted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: accent || P.navy }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: P.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SplitBar({ aLabel, bLabel, aPct, bPct }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ height: 6, borderRadius: 3, display: 'flex', overflow: 'hidden', background: P.border }}>
        <div style={{ width: `${aPct}%`, background: P.navy }} />
        <div style={{ width: `${bPct}%`, background: P.pink }} />
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11, color: P.muted }}>
        <span><span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:P.navy, marginRight:4, verticalAlign:'middle' }}/>
          {aLabel} {aPct.toFixed(1)}%</span>
        <span><span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:P.pink, marginRight:4, verticalAlign:'middle' }}/>
          {bLabel} {bPct.toFixed(1)}%</span>
      </div>
    </div>
  );
}

function PlatformRow({ color, name, impressions, installs, cpi }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding: '10px 0', borderBottom: `1px solid ${P.border}` }}>
      <span style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, fontWeight:600, color:P.navy }}>
        <span style={{ width:8, height:8, borderRadius:'50%', background:color, display:'inline-block' }}/>
        {name}
      </span>
      <span style={{ display:'flex', gap:24 }}>
        {[['impression', fmt(impressions)], ['install', fmt(installs)], ['CPI', fmtE(cpi)]].map(([k,v])=>(
          <span key={k} style={{ textAlign:'right' }}>
            <div style={{ fontSize:13, fontWeight:600, color: k==='CPI' ? P.pink : P.navy }}>{v}</div>
            <div style={{ fontSize:10, color:P.muted }}>{k}</div>
          </span>
        ))}
      </span>
    </div>
  );
}

function DeviceCard({ name, impressions, installs, cpi, spent }) {
  return (
    <div style={{ border:`1px solid ${P.border}`, borderRadius:8, padding:14, flex:1 }}>
      <div style={{ fontSize:11, fontWeight:600, letterSpacing:'0.5px', textTransform:'uppercase',
                    color:P.muted, marginBottom:10 }}>{name}</div>
      {[
        ['Impression', fmt(impressions)],
        ['Installazioni', fmt(installs), P.navy],
        ['CPI', fmtE(cpi)],
        ['Speso', fmtE(spent)],
      ].map(([k,v,c])=>(
        <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0',
                              fontSize:12 }}>
          <span style={{ color:P.muted }}>{k}</span>
          <span style={{ fontWeight:600, color: c||P.text }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function RegionTable({ regions }) {
  const max = Math.max(...regions.map(r => Number(r.installs)||0), 1);
  return (
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
      <thead>
        <tr>
          {['Regione','Impr.','Install','CPI','Rate'].map(h=>(
            <th key={h} style={{ fontSize:10, fontWeight:600, color:P.muted, textAlign: h==='Regione'?'left':'right',
                                 padding:'4px 6px', borderBottom:`1px solid ${P.border}`, letterSpacing:'0.3px' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {regions.map(r=>{
          const pct = ((Number(r.installs)||0)/max*100).toFixed(0);
          const hi  = (Number(r.rate)||0) >= 0.40;
          return (
            <tr key={r.name}>
              <td style={{ padding:'6px 6px', borderBottom:`1px solid ${P.border}`, fontWeight:600, color:P.navy }}>{r.name}</td>
              <td style={{ padding:'6px 6px', borderBottom:`1px solid ${P.border}`, textAlign:'right', color:P.text }}>{fmt(r.impressions)}</td>
              <td style={{ padding:'6px 6px', borderBottom:`1px solid ${P.border}`, textAlign:'right' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, justifyContent:'flex-end' }}>
                  <div style={{ width:40, height:4, background:'#E8F1F8', borderRadius:2, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:P.blue, borderRadius:2 }}/>
                  </div>
                  <span style={{ fontWeight:600, color: hi ? P.pink : P.text }}>{fmt(r.installs)}</span>
                </div>
              </td>
              <td style={{ padding:'6px 6px', borderBottom:`1px solid ${P.border}`, textAlign:'right', color:P.text }}>{fmtE(r.cpi)}</td>
              <td style={{ padding:'6px 6px', borderBottom:`1px solid ${P.border}`, textAlign:'right',
                           fontWeight: hi?600:400, color: hi?P.pink:P.text }}>{fmtP(r.rate)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DemoBar({ age, femaleInstalls, maleInstalls, maxVal }) {
  const fW = ((femaleInstalls||0)/maxVal*120).toFixed(0);
  const mW = ((maleInstalls||0)/maxVal*120).toFixed(0);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:11 }}>
      <span style={{ width:36, color:P.muted, textAlign:'right', flexShrink:0 }}>{age}</span>
      <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <div style={{ width:`${fW}px`, height:8, background:P.navy, borderRadius:2 }}/>
          <span style={{ color:P.text, fontWeight:600 }}>{fmt(femaleInstalls)}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <div style={{ width:`${mW}px`, height:8, background:P.blue, borderRadius:2 }}/>
          <span style={{ color:P.muted }}>{fmt(maleInstalls)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── main component ── */
export default function AppInstallTemplate({ data }) {
  const d = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return EMPTY_DATA;
    return data;
  }, [data]);

  const ov  = d.overview       || {};
  const fb  = d.platformSplit?.facebook  || {};
  const ig  = d.platformSplit?.instagram || {};
  const and = d.devices?.android || {};
  const ios = d.devices?.ios    || {};
  const regions = d.regionsAndroid || [];
  const demo    = d.demographics   || [];

  const fbImp = Number(fb.impressions)||0;
  const igImp = Number(ig.impressions)||0;
  const totImp = fbImp + igImp || 1;
  const fbPct = fbImp/totImp*100;
  const igPct = igImp/totImp*100;

  const maxDemoInstalls = Math.max(
    ...demo.map(d => Math.max(Number(d.femaleInstalls)||0, Number(d.maleInstalls)||0)), 1
  );

  return (
    <div style={{ background: P.bg, padding: 32, fontFamily: "'Segoe UI', system-ui, sans-serif", minWidth: 720 }}>

      {/* HEADER */}
      <div style={{ background: P.navy, borderRadius: 12, padding: '18px 24px',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    marginBottom: 20 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
            <div style={{ background: P.pink, width:28, height:28, borderRadius:'50%',
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
              </svg>
            </div>
            <span style={{ background:'white', borderRadius:6, padding:'2px 8px',
                           fontSize:11, fontWeight:600, color:P.navy }}>
              {d.client || 'CLIENTE'}
            </span>
          </div>
          <div style={{ color:'white', fontSize:15, fontWeight:600 }}>{d.campaign || 'Report App Install'}</div>
          <div style={{ color:'rgba(255,255,255,0.65)', fontSize:12, marginTop:2 }}>{d.period}</div>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {['META','App Install'].map(tag=>(
            <span key={tag} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)',
                                     borderRadius:20, padding:'3px 12px', color:'rgba(255,255,255,0.8)',
                                     fontSize:11 }}>{tag}</span>
          ))}
        </div>
      </div>

      {/* KPI ROW */}
      <p style={sectionLabel}>Performance overview · META</p>
      <div style={{ display:'flex', gap:8, marginBottom:18, flexWrap:'wrap' }}>
        <KpiCard label="App installs"  value={fmt(ov.installs)}    sub={`CPI ${fmtE(ov.cpi)}`}      accent={P.pink} />
        <KpiCard label="Reach"         value={fmt(ov.reach)}       sub={ov.targetCover ? `Cover ${fmtP(ov.targetCover)}` : ''} />
        <KpiCard label="Impression"    value={fmt(ov.impressions)}  sub={ov.audience ? `Audience ${ov.audience}` : ''} />
        <KpiCard label="Interazioni"   value={fmt(ov.interactions)} sub={`CPE ${fmtE(ov.cpe)}`} />
        <KpiCard label="Importo speso" value={fmtE(ov.spent)}      sub={`CPM ${fmtE(ov.cpm)}`}     accent={P.navy} />
      </div>

      {/* PLATFORM + DEVICE */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
        <div style={card}>
          <p style={sectionLabel}>Piattaforma</p>
          <SplitBar aLabel="Facebook" bLabel="Instagram" aPct={fbPct} bPct={igPct} />
          <PlatformRow color={P.navy} name="Facebook"  impressions={fb.impressions}  installs={fb.installs}  cpi={fb.cpi} />
          <PlatformRow color={P.pink} name="Instagram" impressions={ig.impressions}  installs={ig.installs}  cpi={ig.cpi} />
        </div>
        <div style={card}>
          <p style={sectionLabel}>Dispositivo</p>
          <div style={{ display:'flex', gap:12 }}>
            <DeviceCard name="Android" impressions={and.impressions} installs={and.installs} cpi={and.cpi} spent={and.spent} />
            <DeviceCard name="iOS"     impressions={ios.impressions} installs={ios.installs} cpi={ios.cpi} spent={ios.spent} />
          </div>
        </div>
      </div>

      {/* REGIONS + DEMOGRAPHICS */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
        <div style={card}>
          <p style={sectionLabel}>Regioni · Android</p>
          {regions.length > 0
            ? <RegionTable regions={regions} />
            : <p style={{ color:P.muted, fontSize:12 }}>Nessun dato regionale disponibile.</p>}
          <p style={{ fontSize:10, color:P.muted, fontStyle:'italic', marginTop:8 }}>
            Solo Android — tracciamento iOS limitato da Apple
          </p>
        </div>
        <div style={card}>
          <p style={sectionLabel}>Demografica · App install</p>
          <div style={{ display:'flex', gap:12, marginBottom:10, fontSize:11, color:P.muted }}>
            <span><span style={{ display:'inline-block',width:8,height:8,borderRadius:2,background:P.navy,marginRight:4,verticalAlign:'middle' }}/>Femmine</span>
            <span><span style={{ display:'inline-block',width:8,height:8,borderRadius:2,background:P.blue,marginRight:4,verticalAlign:'middle' }}/>Maschi</span>
          </div>
          {demo.length > 0
            ? demo.map(row => (
                <DemoBar
                  key={row.age}
                  age={row.age}
                  femaleInstalls={row.femaleInstalls}
                  maleInstalls={row.maleInstalls}
                  maxVal={maxDemoInstalls}
                />
              ))
            : <p style={{ color:P.muted, fontSize:12 }}>Nessun dato demografico disponibile.</p>}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ textAlign:'center', fontSize:11, color:P.muted, paddingTop:8 }}>
        Gallicantus Italia S.r.l. &nbsp;·&nbsp; report.gallicantus.it &nbsp;·&nbsp; Template: App Install
      </div>
    </div>
  );
}
